import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';

import { GraphQLClient } from 'graphql-request';
import { DateTime } from 'luxon';

import {
	ACCESS_TOKEN_KEY,
	getUserRole,
	getAccessToken,
	getAppUser,
} from '@/constants';

import config from '@/config';
import { AuthLogin } from '@/config.type';

export const AUTH_KEY = 'authenticated';

export const AUTH_EXPIRATION_DATE_KEY = 'subscription-expiration-date';
export const AUTH_SUBJECT_KEY = 'authentication-subject';

// Used by JWT authentication method
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const ACCESS_TOKEN_EXPIRATION_KEY = 'access_token_expiration_date';
export const REFRESH_TOKEN_EXPIRATION_KEY = 'refresh_token_expiration_date';
export const FEATURES_KEY = 'features';
export const IS_SUBSCRIBED_KEY = 'is_subscribed';

const GRAPHQL_URL = `${config.build.host}/graphql`;

export function useAuth(props?: AuthProps) {
	const search = useSearch({ strict: false });

	useEffect(() => {
		if (!config?.app?.auth?.enabled) {
			return;
		}

		const authType = config.app.auth.type;
		const redirect = props?.redirect;

		if (authType === 'login') {
			authLogin(redirect);
			return;
		}

		if (authType === 'jwt') {
			authJwt(redirect);
			return;
		}

		if (authType === 'token' && search) {
			const { token } = search;
			if (!token) {
				return;
			}
			authToken({
				token,
				redirect,
			});

			return;
		}
	}, [search]);
}

interface AuthProps {
	redirect?: boolean;
}

export function useQuery() {
	const { search } = useLocation();

	return useMemo(() => new URLSearchParams(search), [search]);
}

function authLogin(redirect?: boolean) {
	if (!redirect) {
		return;
	}
	if (!localStorage.getItem(AUTH_KEY)) {
		window.location.href = '/';
		return;
	}
	const userString = localStorage.getItem('user');
	if (userString === null) {
		window.location.href = '/';
		return;
	}
	const user: AuthenticateUserResponse = JSON.parse(userString);
	if (!user.digitalAccess) {
		window.location.href = '/';
		return;
	}
}

function authJwt(redirect?: boolean) {
	if (!redirect) {
		return;
	}

	if (!localStorage.getItem(AUTH_KEY)) {
		logoutUserJwt();
		return;
	}

	const now = DateTime.now();
	const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
	const subExp = localStorage.getItem(AUTH_EXPIRATION_DATE_KEY);
	const refreshExp = localStorage.getItem(REFRESH_TOKEN_EXPIRATION_KEY);
	const accessExp = localStorage.getItem(ACCESS_TOKEN_EXPIRATION_KEY);
	if (!subExp || !refreshExp || !accessExp || !refreshToken) {
		// Date doesn't exist so i logout
		logoutUserJwt();
		return;
	}

	const subExpDateTime = DateTime.fromJSDate(new Date(subExp));
	const refreshExpDateTime = DateTime.fromJSDate(new Date(refreshExp));
	const accessExpDateTime = DateTime.fromJSDate(new Date(accessExp));

	// If now is larger than the expiration of the subscription or
	// larger than the refresh token expiration date. Logout the use
	if (now >= subExpDateTime || now >= refreshExpDateTime) {
		logoutUserJwt();
		return;
	}

	// If true the user access token is valid
	if (now < accessExpDateTime) {
		return;
	}

	// In here the access token is expired but the app is offline
	if (!navigator.onLine) {
		return;
	}

	// We are going to request a new access token using the refresh token
	refreshAccessToken(refreshToken)
		.then(() => {
			console.log('Token refreshed successfully');
		})
		.catch(err => {
			alert(err.message);
		});
}

function logoutUserJwt() {
	const jwtKeys = [
		AUTH_KEY,
		ACCESS_TOKEN_KEY,
		REFRESH_TOKEN_KEY,
		AUTH_SUBJECT_KEY,
		AUTH_EXPIRATION_DATE_KEY,
		ACCESS_TOKEN_EXPIRATION_KEY,
		REFRESH_TOKEN_EXPIRATION_KEY,
	];

	removeLocalStorageKeys(jwtKeys);
	window.location.href = '/';
}

function removeLocalStorageKeys(keys: Array<string>) {
	for (const key of keys) {
		if (localStorage.getItem(key)) {
			localStorage.removeItem(key);
		}
	}
}

function authToken({ token, redirect }: AuthTokenArgs) {
	const navigate = useNavigate();
	const pathname = useLocation({
		select: location => location.pathname,
	});
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const expirationDate = localStorage.getItem(AUTH_EXPIRATION_DATE_KEY);

	if (isAuthenticated) {
		if (token) {
			navigate({
				to: pathname,
				search: {},
			});
		}

		return;
	}
	if (!token) {
		if (!redirect) {
			return;
		}

		if (!isAuthenticated || !expirationDate) {
			navigate({
				to: '/',
				search: {},
			});
			return;
		}

		const expirationDateTime = DateTime.fromJSDate(
			new Date(expirationDate),
		);
		if (!expirationDateTime.isValid) {
			navigate({
				to: '/',
				search: {},
			});
			return;
		}
		const currentDateTime = DateTime.fromJSDate(new Date());

		const isValidSubscription: boolean =
			currentDateTime <= expirationDateTime;
		if (!isValidSubscription) {
			navigate({
				to: '/',
				search: {},
			});
			return;
		}
		return;
	}

	authWithToken(token)
		.then(() => {
			navigate({
				to: pathname,
				search: {},
			});
		})
		.catch((err: Error) => {
			if (err.message !== 'Failed to fetch') {
				alert(`Error: ${err.message}`);
			}
		});
}

interface AuthTokenArgs {
	token: string | null;
	redirect?: boolean;
}

async function authWithToken(token: string) {
	const url = [
		'https://api.canvasflow.io/v1/apptoken?',
		new URLSearchParams({ token }).toString(),
	].join('');

	console.log(`URL: `, url);
	const r = await fetch(url);
	const response = await r.json();
	if (response.status === 'success') {
		const { payload } = response;
		localStorage.setItem(AUTH_KEY, 'true');
		localStorage.setItem(AUTH_EXPIRATION_DATE_KEY, payload.expirationDate);
		return;
	}

	if (response.error) {
		throw new Error(response.error);
	}

	throw new Error('Failed authentication');
}

export async function authenticateViaJwt(
	args: LoginArgs,
): Promise<AuthenticateUserResponse> {
	const appKey = config.build.appKey;
	const type = config?.app.auth.type;
	if (type !== 'jwt') {
		return {
			digitalAccess: false,
			features: [],
		};
	}
	const auth: AuthLogin = config.app.auth;
	const url = `${auth.host}/authorize`;
	const req = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			// 'X-Canvasflow-App-Key': appKey,
		},
		body: JSON.stringify({ ...args, client_id: appKey }),
	};

	const rawResponse = await fetch(url, req);
	const content = await rawResponse.json();
	if (content.error) {
		throw new Error(content.error);
	}

	const { access_token, refresh_token, expires } = content;
	const accessTokenData = parseJwt(access_token);
	const refreshTokenData = parseJwt(refresh_token);

	const accessExpirationDate = DateTime.now()
		.plus({ seconds: expires })
		.toISO();
	const refreshExpirationDate = DateTime.fromSeconds(
		refreshTokenData.exp,
	).toISO();

	const subExpirationDate =
		accessTokenData?.entitlements?.subscription_expiration_date;
	if (!subExpirationDate) {
		throw new Error('User do not have subscription or is expired');
	}

	// Check if the expiration date is after now
	if (DateTime.now() >= DateTime.fromJSDate(new Date(subExpirationDate))) {
		throw new Error('User subscription has expired');
	}

	const features: Array<string> = accessTokenData?.entitlements?.features
		? accessTokenData.entitlements.features
		: [];

	localStorage.setItem(AUTH_KEY, 'true');
	localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
	localStorage.setItem(ACCESS_TOKEN_EXPIRATION_KEY, accessExpirationDate);

	localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
	if (refreshExpirationDate) {
		localStorage.setItem(
			REFRESH_TOKEN_EXPIRATION_KEY,
			refreshExpirationDate,
		);
	}

	localStorage.setItem(AUTH_EXPIRATION_DATE_KEY, subExpirationDate);

	localStorage.setItem(AUTH_SUBJECT_KEY, accessTokenData.sub);
	localStorage.setItem(FEATURES_KEY, JSON.stringify(features));

	return {
		uid: accessTokenData.sub,
		digitalAccess: true,
		expirationDate: subExpirationDate,
		email: '',
		features,
	};
}

export async function refreshAccessToken(token: string) {
	const queryParams = new URLSearchParams({
		refresh_token: token,
	}).toString();
	const type = config?.app.auth.type;
	if (type !== 'login' && type !== 'token') {
		return;
	}
	const url = `${config?.app?.auth?.host}/token?${queryParams}`;
	const rawResponse = await fetch(url);
	const content = await rawResponse.json();
	if (content.error) {
		logoutUserJwt();
		throw new Error(content.error);
	}
	const { access_token, expires } = content;
	const tokenData = parseJwt(access_token);
	const expirationDate = DateTime.now()
		.plus({ seconds: expires })
		.toISO();

	const subExpirationDate =
		tokenData?.entitlements?.subscription_expiration_date;
	if (!subExpirationDate) {
		logoutUserJwt();
		throw new Error('User do not have subscription or is expired');
	}

	// Check if the expiration date is after now
	if (DateTime.now() >= DateTime.fromJSDate(new Date(subExpirationDate))) {
		logoutUserJwt();
		throw new Error('User subscription has expired');
	}

	localStorage.setItem(AUTH_KEY, 'true');
	localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
	localStorage.setItem(AUTH_EXPIRATION_DATE_KEY, subExpirationDate);
	localStorage.setItem(ACCESS_TOKEN_EXPIRATION_KEY, expirationDate);
	localStorage.setItem(AUTH_SUBJECT_KEY, tokenData.sub);
}

export async function authenticateViaLogin(
	args: LoginArgs,
): Promise<AuthenticateUserResponse> {
	const type = config?.app.auth.type;
	if (type !== 'login' && type !== 'token') {
		return {
			digitalAccess: false,
			features: [],
		};
	}

	const url = `${config?.app?.auth?.host}/login`;
	const req =
		config.build.shortTitle === 'P&W'
			? {
					method: 'POST',
					body: JSON.stringify({ data: args }),
			  }
			: {
					method: 'POST',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(args),
			  };
	const rawResponse = await fetch(url, req);
	const content = await rawResponse.json();
	if (content?.response?.login !== 'SUCCESS') {
		if (content?.response?.login_fail) {
			throw new Error(content?.response?.login_fail);
		}

		throw new Error('Failed login');
	}

	const { response } = content;
	const {
		uid,
		expiration_date,
		subscription_level,
		subscription_status,
		email,
		digital_access,
	} = response;

	const userResponse: AuthenticateUserResponse = {
		uid,
		expirationDate: expiration_date,
		email,
		digitalAccess: digital_access === 'Y',
		subscriptionLevel: subscription_level,
		subscriptionStatus: subscription_status,
		features: [],
	};
	localStorage.setItem('user', JSON.stringify(userResponse));
	return userResponse;
}

export interface LoginArgs {
	username: string;
	password: string;
}

export interface AuthenticateUserResponse {
	uid?: string;
	expirationDate?: string;
	email?: string;
	subscriptionLevel?: string;
	digitalAccess: boolean;
	subscriptionStatus?: string;
	features: Array<string>;
}

export function parseJwt(token: string) {
	const base64Url = token.split('.')[1];
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(function(c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			})
			.join(''),
	);

	return JSON.parse(jsonPayload);
}

export const useOnClickOutside = (ref: any, handler: any) => {
	useEffect(() => {
		const listener = (event: any) => {
			if (!ref.current || ref.current.contains(event.target)) {
				return;
			}
			handler(event);
		};

		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);

		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, handler]);
};

export interface SignUpProps {
	onClickLogIn: () => void;
	onCloseDialog: () => void;
	onSuccess: () => void;
}

export function getGraphQLClient(): GraphQLClient {
	const headers = getHTTPHeaders();

	const client = new GraphQLClient(GRAPHQL_URL, {
		headers: headers,
	});

	return client;
}

export function getHTTPHeaders(): HeadersInit {
	const appKey = config.build.appKey;
	const headers: any = {
		'app-key': appKey,
		'content-type': 'application/json',
		'x-timestamp': `${new Date().getTime()}`,
	};
	const userRole = getUserRole();
	if (userRole) {
		headers['app-user-role'] = userRole;
	}

	const appUser = getAppUser();
	if (appUser) {
		headers['app-user-id'] = appUser;
	}

	const token = getAccessToken();
	if (token) {
		headers['Authorization'] = token;
	}
	return headers;
}
